/**
 * workflow.types.ts — shared TS types for the Workflows feature.
 *
 * Mirrors the backend contract exactly (see AGENTS.md task spec / backend
 * Workflows REST API). This is the single source of truth for these shapes
 * on the frontend — the service layer and every component/hook here import
 * from this file rather than redeclaring the contract.
 */

export type NodeCategory = "trigger" | "action" | "logic";
export type NodeType = "trigger" | "action" | "condition";
/** "out" for action/trigger nodes; a condition node's option id otherwise
 * (historically "yes"/"no", now any id from its `options` list). */
export type OutputPort = string;

/** How a field should be edited — 'text'/'textarea' are free-form, 'select' is a fixed-but-editable
 * list, the 'select-*' kinds are backed by a real list endpoint. */
export type NodeDefArgFieldType =
  | "text"
  | "textarea"
  | "select"
  | "select-client"
  | "select-meeting"
  | "select-contact"
  | "select-template"
  | "select-skills"
  | "select-notes-folder"
  | "select-meeting-type";

export interface NodeDefArgFieldOption {
  value: string;
  label: string;
}

/** One field a NodeDef's action wants filled in, rendered in the properties panel per its `type`. */
export interface NodeDefArgField {
  key: string;
  label: string;
  defaultTemplate: string;
  placeholder?: string;
  type?: NodeDefArgFieldType;
  options?: NodeDefArgFieldOption[];
  multi?: boolean;
  templateType?: string;
  syncToField?: string;
  advanced?: boolean;
  /** The action fails without a value here — the properties panel flags it in red when empty. */
  required?: boolean;
  /** Names a sibling argField on the same node whose current value scopes this field's own option list (e.g. Meeting scoped to this node's own Client). */
  dependsOn?: string;
  /** For type 'textarea' — quick-insert chips for template tokens genuinely
   * resolvable in this node's context. */
  insertTokens?: { token: string; label: string }[];
}

/** A catalog entry describing one kind of node the palette can add. */
export interface NodeDef {
  subtype: string;
  category: NodeCategory;
  label: string;
  description: string;
  tool?: string;
  argFields?: NodeDefArgField[];
  /** Only for trigger nodes — fields that narrow WHEN the trigger fires,
   * stored on the workflow's own trigger.config rather than a canvas node's
   * config.args. */
  configFields?: NodeDefArgField[];
}

/** 'ai' (default) is the free-text LLM-reasoned condition; 'field' is a deterministic
 * IFTTT-style "IF <field> <is/is not> <value>" check with no LLM call. */
export type ConditionMode = "ai" | "field";
export type ConditionOperator = "is" | "is_not" | "is_empty" | "is_not_empty" | "contains" | "gt" | "gte" | "lt" | "lte";

/** One branch an 'ai'-mode condition node can route to. `id` is what edges
 * wire from (stable — never renamed once created); `label` is the
 * adviser-facing text shown on the port and judged against by the model. */
export interface ConditionOption {
  id: string;
  label: string;
}

/** One branch an 'ai'-mode condition node can route to. `id` is what edges
 * wire from (stable — never renamed once created); `label` is the
 * adviser-facing text shown on the port and judged against by the model. */
export interface ConditionOption {
  id: string;
  label: string;
}

export interface WorkflowNodeConfig {
  label?: string;
  prompt?: string;
  yesLabel?: string;
  noLabel?: string;
  /** 'ai'-mode's outcomes. Unset (or empty) means the plain Yes/No pair below —
   * see `getConditionOptions`, the one place that decides a condition node's
   * actual port list. 'field' mode never sets this; it's always binary. */
  options?: ConditionOption[];
  args?: Record<string, string>;
  conditionMode?: ConditionMode;
  field?: string;
  operator?: ConditionOperator;
  value?: string;
  /** User-resized card dimensions (px) — unset means the CSS default. Persisted
   * per-node so a long label or a condition with several branches can get more
   * room without affecting every other node on the canvas. */
  width?: number;
  height?: number;
}

/** The single source of truth for "what are this condition node's output
 * ports" — used when rendering ports, editing them, and computing edge
 * geometry, so all three can never disagree. Mirrored on the backend
 * (conditionEvaluator.ts) for execution routing; keep both in sync. */
export function getConditionOptions(config: WorkflowNodeConfig): ConditionOption[] {
  if (config.options && config.options.length > 0) return config.options;
  return [
    { id: "yes", label: config.yesLabel || "Yes" },
    { id: "no", label: config.noLabel || "No" },
  ];
}

export type ConditionFieldDataType = "string" | "number" | "date" | "boolean";

export interface ConditionFieldDef {
  key: string;
  label: string;
  source: "meeting" | "client";
  path: string;
  /** A fixed, known set of choices — absent for a free date/number/text field, which renders a real input instead of a dropdown. */
  options?: string[];
  dataType?: ConditionFieldDataType;
}

export interface WorkflowNode {
  id: string;
  type: NodeType;
  subtype: string;
  x: number;
  y: number;
  config: WorkflowNodeConfig;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  sourcePort: OutputPort;
  target: string;
  targetPort: "in";
}

export interface WorkflowTrigger {
  type: string;
  config?: Record<string, unknown>;
}

export interface WorkflowDefinition {
  _id: string;
  orgId: string;
  name: string;
  description?: string;
  enabled: boolean;
  hasConfirmedActivation: boolean;
  trigger: WorkflowTrigger;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  createdAt: string;
  updatedAt: string;
}

export type WorkflowRunStatus = "running" | "waiting_input" | "completed" | "failed" | "stopped";

export interface WorkflowPendingInput {
  stepId: string;
  tool?: string;
  label?: string;
  fields: string[];
  suggested?: Record<string, string>;
}
export type WorkflowRunStepStatus = "done" | "skipped" | "failed" | "not_reached";

export interface WorkflowRunStep {
  stepId: string;
  tool?: string;
  label: string;
  status: WorkflowRunStepStatus;
  args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  verdict?: string;
  verdictLabel?: string;
  reasoning?: string;
}

export interface WorkflowRun {
  _id: string;
  workflowId: string;
  status: WorkflowRunStatus;
  stopRequested: boolean;
  pendingInput?: WorkflowPendingInput;
  steps: WorkflowRunStep[];
  startedAt: string;
  completedAt?: string;
}

export interface WorkflowToolCatalog {
  triggers: NodeDef[];
  actions: NodeDef[];
  logic: NodeDef[];
  conditionFields: ConditionFieldDef[];
}

/** Visual-only state derived on the frontend from a WorkflowRun for NodeCard rendering. */
export type NodeRunState =
  | "pending"
  | "active"
  | "done"
  | "skipped"
  | "failed"
  | "not_reached";
